import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { applyTitle } from "./hooks/useDocumentTitle";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Proposals from "./pages/Proposals";
import ProposalEditor from "./pages/ProposalEditor";
import Templates from "./pages/Templates";
import Catalog from "./pages/Catalog";
import Clients from "./pages/Clients";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import AdminCompanies from "./pages/AdminCompanies";
import AdminUsers from "./pages/AdminUsers";
import AdminAI from "./pages/AdminAI";
import AdminSettings from "./pages/AdminSettings";
import AdminEmails from "./pages/AdminEmails";
import AdminBilling from "./pages/AdminBilling";
import AdminSenderDomains from "./pages/AdminSenderDomains";
import ProposalAnalytics from "./pages/ProposalAnalytics";
import PublicProposal from "./pages/PublicProposal";
import PublicProposalGroup from "./pages/PublicProposalGroup";
import TeamGroups from "./pages/TeamGroups";
import ProposalCoach from "./pages/ProposalCoach";
import Mcp from "./pages/Mcp";
import PartnerNewProposal from "./pages/PartnerNewProposal";
import PartnerProposals from "./pages/PartnerProposals";
import PartnerProposalDetail from "./pages/PartnerProposalDetail";
import PartnerGroupDetail from "./pages/PartnerGroupDetail";
import PartnerAccount from "./pages/PartnerAccount";

// Un partenaire (revendeur) n'a accès qu'à /partner/* — le backend renvoie 403 sur toute
// route staff de toute façon, cette garde évite juste d'afficher un écran cassé. Centralisé
// ici (plutôt que route par route) : Protected enveloppe aussi bien /proposals/coach et
// /proposals/:id/edit (hors Layout) que tout l'arbre <Layout/>.
function Protected({ children }) {
  const { user, ready, role } = useAuth();
  const location = useLocation();
  if (!ready || user === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "partner" && !location.pathname.startsWith("/partner")) return <Navigate to="/partner/proposals" replace />;
  return children;
}

function RequireSuperAdmin({ children }) {
  const { user, isSuperadmin, ready } = useAuth();
  if (!ready || user === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isSuperadmin) return <Navigate to="/dashboard" replace />;
  return children;
}

// Titres d'onglet pour les routes statiques. Les pages dynamiques (éditeur,
// viewer public, analytics) posent leur propre titre via useDocumentTitle.
const ROUTE_TITLES = {
  "/dashboard": "Tableau de bord",
  "/proposals": "Propositions",
  "/templates": "Modèles",
  "/catalog": "Catalogue",
  "/clients": "Clients",
  "/settings": "Paramètres",
  "/mcp": "MCP",
  "/billing": "Facturation",
  "/partner/new": "Nouvelle proposition",
  "/partner/proposals": "Mes propositions",
  "/partner/account": "Mon compte",
  "/login": "Connexion",
  "/register": "Créer un compte",
  "/forgot-password": "Mot de passe oublié",
  "/reset-password": "Réinitialisation du mot de passe",
  "/proposals/coach": "Assistant IA",
  "/admin/companies": "Sociétés",
  "/admin/users": "Utilisateurs",
  "/admin/ai": "Moteur IA",
  "/admin/settings": "Paramètres plateforme",
  "/admin/emails": "Modèles d'email",
  "/admin/billing": "Facturation",
  "/admin/sender-domains": "Domaines d'envoi",
};

function TitleManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const t = ROUTE_TITLES[pathname];
    if (t) applyTitle(t);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <TitleManager />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/p/:publicId" element={<PublicProposal />} />
            <Route path="/g/:groupId" element={<PublicProposalGroup />} />
            <Route path="/proposals/coach" element={<Protected><ProposalCoach /></Protected>} />
            <Route path="/proposals/:id/edit" element={<Protected><ProposalEditor /></Protected>} />
            <Route path="/templates/:id/edit" element={<Protected><ProposalEditor mode="template" /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/proposals" element={<Proposals />} />
              <Route path="/proposals/:id/analytics" element={<ProposalAnalytics />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/groups" element={<TeamGroups />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/mcp" element={<Mcp />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/partner/new" element={<PartnerNewProposal />} />
              <Route path="/partner/proposals" element={<PartnerProposals />} />
              <Route path="/partner/proposals/:id" element={<PartnerProposalDetail />} />
              <Route path="/partner/groups/:id" element={<PartnerGroupDetail />} />
              <Route path="/partner/account" element={<PartnerAccount />} />
              <Route path="/admin/companies" element={<RequireSuperAdmin><AdminCompanies /></RequireSuperAdmin>} />
              <Route path="/admin/users" element={<RequireSuperAdmin><AdminUsers /></RequireSuperAdmin>} />
              <Route path="/admin/ai" element={<RequireSuperAdmin><AdminAI /></RequireSuperAdmin>} />
              <Route path="/admin/settings" element={<RequireSuperAdmin><AdminSettings /></RequireSuperAdmin>} />
              <Route path="/admin/emails" element={<RequireSuperAdmin><AdminEmails /></RequireSuperAdmin>} />
              <Route path="/admin/billing" element={<RequireSuperAdmin><AdminBilling /></RequireSuperAdmin>} />
              <Route path="/admin/sender-domains" element={<RequireSuperAdmin><AdminSenderDomains /></RequireSuperAdmin>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
